import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface EstimatorHookData {
  website: string;
  enabled: boolean;
  clientId?: string;
}

export interface CheckHookPermissionData {
  clientId: string;
  referrer: string;
}

export interface EstimatorHookResponse {
  id: string;
  userId: string;
  website: string;
  enabled: boolean;
  clientId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EstimatorHookApiResponse {
  success: boolean;
  message: string;
  data?: {
    estimatorHook: EstimatorHookResponse;
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// SUBMIT ESTIMATOR HOOK ACTION
export const updateEstimatorHook = createAsyncThunk<
  EstimatorHookApiResponse,
  EstimatorHookData,
  { rejectValue: string }
>(
  "estimatorHook/updateEstimatorHook",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createApiClient.post<EstimatorHookApiResponse>(
        "/estimator-hook/update",
        formData
      );

      if (response.data.success) {
        toast.success(
          response.data.message || "Estimator hook updated successfully"
        );
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to update estimator hook");
        return rejectWithValue(
          response.data.message || "Failed to update estimator hook"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as EstimatorHookApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// CHECK HOOK PERMISSION ACTION
export const checkHookPermission = createAsyncThunk<
  EstimatorHookApiResponse,
  CheckHookPermissionData,
  { rejectValue: string }
>(
  "estimatorHook/checkHookPermission",
  async ({ clientId, referrer }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.post<EstimatorHookApiResponse>(
        "/estimator-hook/check",
        { clientId, referrer }
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message || "Not authorized to embed this estimator"
        );
        return rejectWithValue(
          response.data.message || "Not authorized to embed this estimator"
        );
      }
    } catch (error) {
      let errorMessage = "Failed to verify permission";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as EstimatorHookApiResponse).message ||
          "Failed to verify permission";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET ESTIMATOR HOOK DETAILS ACTION
export const getMyEstimatorHookDetail = createAsyncThunk<
  EstimatorHookApiResponse,
  void,
  { rejectValue: string }
>("estimatorHook/getMyEstimatorHookDetail", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<EstimatorHookApiResponse>(
      "/estimator-hook/my-details"
    );

    if (response.data.success) {
      toast.success("Estimator Data Fetched");
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to retrieve estimator hook details"
      );
      return rejectWithValue(
        response.data.message || "Failed to retrieve estimator hook details"
      );
    }
  } catch (error) {
    let errorMessage = "Failed to retrieve estimator hook details";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as EstimatorHookApiResponse).message ||
        "Failed to retrieve estimator hook details";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
